import React from 'react'
import { Paper, Box, Button as MuiButton, Popper, Grow, MenuItem, MenuList, ClickAwayListener } from '@mui/material';
import { Table, Row, Col } from 'antd';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import usePagination from '../../../common/table/Pagination';
import { convertPxToRem, MESSAGE_TYPE, messageManager, translate } from '../../../helper';
import { formatCurrency } from '../../../utils/CommonFunction';
import TextBase from '../../../common/text';
import { css } from 'aphrodite';
import { LargeCreateButton } from '../../../modules/child-accounts';
import { ReactComponent as CreatePlusIcon } from '../../../assets/image/svg/create-plus.svg';
import { calculateSubscriptionSummary } from './const';
import { useCancelChildSubscription, useReactivateChildSubscription } from '../../../hooks/useSubscriptions';
import { ConfirmModal } from '../../../common/confirm-modal';
import ConfirmDialog from '../../../common/confirm-modal/ConfirmDialog';
import { deleteChildAccountApiCall } from '../../../modules/child-accounts-detail/Action';
import { Colors } from '../../../themes/Colors';
import { useScreenSubscription } from '../screenProviderLocationAccount';
import { WORK_FLOW_LOCATION_ACCOUNT } from '../const';
import { styles } from '../styles';
import { LocationsColumnDetails } from './locationsColumnDetails';
import APIManager from '../../../connection/ApiManager';
import { usePermissions } from '../../../hooks/usePermissions';
import Search from '../../../common/search';
import CustomSpinner from '../../../common/CustomSpinner';

// Plan filter options
const PLAN_FILTER_OPTIONS = [
  { value: 'all', label: 'Show All' },
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'pro', label: 'Pro' },
];

export const ActiveLocationSubscription = () => {
  const [showActivateSubModal, setShowActivateSubModal] = React.useState(false);
  const [showCancelSubModal, setShowCancelSubModal] = React.useState(false);
  const [showReactivateModal, setShowReactivateModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [childAccountId, setChildAccountId] = React.useState('');
  const { navigateTo, setChildAccount, triggerRefresh } = useScreenSubscription();
  const [isLocationDeleting, setIsLocationDeleting] = React.useState(false);
  const [hasPendingOrders, setHasPendingOrders] = React.useState(false);
  const { canEdit } = usePermissions();
  const canEditSubscription = canEdit('subscriptions');

  // Search and filter states
  const [searchText, setSearchText] = React.useState('');
  const [planFilter, setPlanFilter] = React.useState('all');
  const [openPlanMenu, setOpenPlanMenu] = React.useState(false);
  const planMenuRef = React.useRef(null);

  // Independent locations state & pagination
  const PAGE_SIZE = 15;
  const [filters, setFilters] = React.useState({ pageNo: 1, limit: PAGE_SIZE });
  const [childAccounts, setChildAccounts] = React.useState([]);
  const [totalLocations, setTotalLocations] = React.useState(0);
  const [locationsLoading, setLocationsLoading] = React.useState(false);
  const [paging] = usePagination(totalLocations, filters, setFilters, locationsLoading);

  const fetchLocations = React.useCallback(async (page = 1, limit = PAGE_SIZE, search = '', subscription = '') => {
    setLocationsLoading(true);
    try {
      const params = { page, page_size: limit };
      if (search) params.search = search;
      if (subscription && subscription !== 'all') params.subscription = subscription;
      
      const { data } = await APIManager.getInstance().getAllLocations(params);
      setChildAccounts(data?.results || []);
      setTotalLocations(data?.count || 0);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchLocations(filters.pageNo, filters.limit, searchText, planFilter);
  }, [filters.pageNo, filters.limit, searchText, planFilter, fetchLocations]);

  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      pageNo: 1
    }));
  }, [searchText, planFilter]);

  const refreshAll = () => {
    triggerRefresh();
    fetchLocations(filters.pageNo, filters.limit, searchText, planFilter);
  };

  const { total } = calculateSubscriptionSummary(childAccounts);

  const onSuccessReactiveSubs = () => {
    messageManager({
      type: MESSAGE_TYPE.success,
      messageShow: 'Successfully subscribed'
    });
    closeReactivateSubConfirm();
    navigateTo(WORK_FLOW_LOCATION_ACCOUNT.ACTIVE_LOCATION_SUBSCRIPTION);
    refreshAll();
  }

  const closeReactivateSubConfirm = () => {
    setShowReactivateModal(false);
    setChildAccountId('')
  };

  const onSuccessChildCancel = () => {
    messageManager({
      type: MESSAGE_TYPE.success,
      messageShow: 'Successfully unsubscribed'
    });
    closeCancelConfirm();
    navigateTo(WORK_FLOW_LOCATION_ACCOUNT.ACTIVE_LOCATION_SUBSCRIPTION);
    refreshAll();
  }

  const handleManage = (childAccount) => {
    setChildAccount(childAccount)
    navigateTo(WORK_FLOW_LOCATION_ACCOUNT.CHILD_ACCOUNTS_DETAIL_PAGE);
  };

  const handleResumeSubscribeChild = (id) => {
    setChildAccountId(id)
    setShowReactivateModal(true)
  }

  const handleResumeMembership = () => {
    if (childAccountId) {
      reactivateChildSubscription({ child_account_id: childAccountId })
    }
  };

  const handleUnsubscribe = () => {
    if (childAccountId) {
      cancelChildSubscription({ cancel_at_period_end: true, ...(childAccountId ? { child_account_id: childAccountId } : {}) })
    }
  };

  const closeCancelConfirm = () => {
    setShowCancelSubModal(false);
    setChildAccountId('')
  };

  const closeActivateSub = () => {
    setShowActivateSubModal(false)
  }

  const handleSubscribeChild = (childAccount) => {
    setChildAccount(childAccount)
    setShowActivateSubModal(true)
  }

  const handleActivateSubscription = () => {
    navigateTo(WORK_FLOW_LOCATION_ACCOUNT.PURCHASE_LOCATION);
    closeActivateSub()
  }


  const handleCancelSelectChild = (id) => {
    setChildAccountId(id)
    setShowCancelSubModal(true)
  }

  const handleDeleteChild = () => {
    if (childAccountId) {
      setIsLocationDeleting(true);
      deleteChildAccountApiCall(childAccountId, () => {
        refreshAll();
        setShowDeleteModal(false);
        setChildAccountId('');
        setIsLocationDeleting(false);
      });
    }
  }

  const columns = LocationsColumnDetails({
    handleCancelSelectChild,
    handleSubscribeChild,
    handleResumeSubscribeChild,
    handleManage,
    setShowDeleteModal,
    setChildAccountId,
    setHasPendingOrders,
    canEditSubscription
  });

  const { cancelChildSubscription, isCancellingChildSubscription } = useCancelChildSubscription(onSuccessChildCancel);
  const { reactivateChildSubscription, isReactivatingChildSubscription } = useReactivateChildSubscription(onSuccessReactiveSubs);

  const subscriptionSummary = () => {
    const cards = [
      { title: 'Total Locations:', value: totalLocations },
      { title: 'Total Location Cost:', value: formatCurrency(total) }
    ];

    return (
      <div style={{ display: 'flex', gap: 'var(--sp-md2)', alignItems: 'stretch', width: '55%' }}>
        {cards.map((c, idx) => (
          <div
            key={idx}
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
              borderRadius: 'var(--sp-md2)',
              border: '1px solid rgba(226, 232, 240, 1)',
              padding: 'calc(var(--sp-md2)*1.8867924) calc(var(--sp-md2)*1.69811)',
              position: 'relative',
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 'calc(var(--sp-md2)*0.85)',

            }}
          >
            <div
              style={{
                color: 'rgba(100, 116, 139, 1)',
                fontWeight: '500',
                fontSize: 'calc(var(--fs-xs)*1.368421)',
                letterSpacing: '0.01em'
              }}
            >
              {c.title}
            </div>
            <div style={{ fontSize: 'calc(var(--fs-xs)*1.7368421)', fontWeight: '700', color: 'rgba(15, 23, 42, 1)' }}>{c.value}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Paper className={css(styles.mainPaperLoc)} sx={{ border: 'none', boxShadow: 'none' }}>

      {/* Search and Filter Row */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 'calc(var(--sp-lg)*1.132075)' }}>
        <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
          <Search
            placeholder="Search locations..."
            setQuery={setSearchText}
            tooltip="Search by location name"
            value={searchText}
            sx={{
              width: 'calc(var(--fs-hero)*8.254)',
              '& .MuiOutlinedInput-input': {
                fontSize: 'var(--fs-md2) !important',
                padding: 'var(--sp-md2) var(--sp-md2*1.4)',
              },
              '& .MuiOutlinedInput-input::placeholder': {
                fontSize: 'var(--fs-md2)',
                padding: 'var(--sp-md2) var(--sp-md2*1.4)',
                lineHeight: '100%',
              },
            }}
          />
        </Col>
        <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--sp-md2)' }}>
          <Box display="flex" alignItems="center" gap="var(--sp-md2)">
            {/* Plan Filter Button */}
            <MuiButton
              ref={planMenuRef}
              onClick={() => setOpenPlanMenu(prev => !prev)}
              sx={{
                textTransform: 'capitalize',
                fontWeight: 700,
                height: 'calc(var(--fs-xl)*2.35) !important',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderRadius: 'var(--sp-md2)',
                fontSize: 'calc(var(--fs-sm2) * 1.015)',
                backgroundColor: '#ffffff !important',
                border: '1.5px solid #e5e7eb !important',
                color: '#374151 !important',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                padding: 'calc(var(--sp-md)*1) calc(var(--sp-md2)*1.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 'calc(var(--sp-xxs)*1.5)',
                '&:hover': {
                  backgroundColor: '#f3f4f6 !important',
                  borderColor: '#d1d5db !important',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)'
                }
              }}>
              <span style={{ fontWeight: 500, color: '#374151' }}>Plan:</span>
              <span style={{ fontWeight: 600, color: '#1A5A78' }}>
                {PLAN_FILTER_OPTIONS.find(o => o.value === planFilter)?.label}
              </span>
              <ArrowDropDownIcon
                sx={{
                  fontSize: 'calc(var(--fs-xl) * 1.132075)',
                  marginLeft: 'auto',
                  transition: 'transform 0.2s ease',
                  transform: openPlanMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#9ca3af'
                }}
              />
            </MuiButton>

            <Popper
              open={openPlanMenu}
              anchorEl={planMenuRef.current}
              placement="bottom-end"
              transition
              disablePortal
              strategy="fixed"
              sx={{ zIndex: 9999, textTransform: 'capitalize' }}
              modifiers={[
                {
                  name: 'sameWidth',
                  enabled: true,
                  phase: 'beforeWrite',
                  requires: ['computeStyles'],
                  fn: ({ state }) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    altBoundary: true,
                    tether: false,
                    padding: 8,
                  },
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                },
              ]}
            >
              {({ TransitionProps, placement }) => (
                <Grow {...TransitionProps} style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom'
                }}>
                  <Paper sx={{ width: '100%' }}>
                    <ClickAwayListener onClickAway={() => setOpenPlanMenu(false)}>
                      <MenuList autoFocusItem>
                        {PLAN_FILTER_OPTIONS.map(option => (
                          <MenuItem
                            key={option.value}
                            selected={option.value === planFilter}
                            onClick={() => {
                              setPlanFilter(option.value);
                              setOpenPlanMenu(false);
                            }}
                            style={{
                              color: option.value === planFilter ? '#1A5A78' : '#000',
                              fontSize: 'var(--fs-md2)',
                              fontWeight: option.value === planFilter ? '600' : '500'
                            }}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>

            {/* Create Location Button */}
            {canEditSubscription && (
              <LargeCreateButton
                style={{
                  textTransform: 'none',
                  padding: 'var(--sp-md2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'calc(var(--sp-md2)*1)'
                }}
                onClick={() => navigateTo(WORK_FLOW_LOCATION_ACCOUNT.LOCATION_ACCOUNT_SUBSCRIPTION)}
              >
                <CreatePlusIcon style={{ width: 'calc(var(--fs-xs)*1.226415)', height: 'calc(var(--fs-xs)*1.226415)', display: 'inline-flex' }} />
                <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1, fontWeight: 600 }}>{translate('createLocation')}</span>
              </LargeCreateButton>
            )}
          </Box>
        </Col>
      </Row>

      {/* Subscription Summary Cards */}
        {subscriptionSummary()}

      <div className={css(styles.containerTable)}>
        <Table
          className='latest-orders-table no-row-hover compact-loc-table'
          style={{ width: '100%' }}
          columns={columns || []}
          components={{
            header: {
              cell: ({ children, ...rest }) => {
                return (
                  <th {...rest} className="loc-sub-header">
                    {children}
                  </th>
                );
              }
            }
          }}
          dataSource={childAccounts || []}
          bordered={false}
          pagination={false}
          loading={{ spinning: locationsLoading, indicator: <CustomSpinner fullPage/> }}
        />

        {!locationsLoading && paging}

        <ConfirmModal
          visible={showActivateSubModal}
          status="warning"
          okBtnText="activateSubscription"
          cancelBtnText="closeSubscription"
          width="600px"
          handleOk={handleActivateSubscription}
          handleCancel={closeActivateSub}
          onClose={closeActivateSub}
        >
          <TextBase
            style={{ fontSize: '24px', textAlign: 'center' }}
            fontWeight={400}
            size={convertPxToRem(14)}
            color={Colors.black}>
            confirmActivateSubscription
          </TextBase>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0px' }} />
        </ConfirmModal>

        <ConfirmModal
          visible={showCancelSubModal}
          status="warning"
          okBtnText="keepSubscription"
          cancelBtnText="cancelSubscription"
          width="600px"
          handleOk={closeCancelConfirm}
          handleCancel={handleUnsubscribe}
          onClose={closeCancelConfirm}
          isLoadingLeft={isCancellingChildSubscription}>
          <TextBase
            style={{ fontSize: '24px', textAlign: 'center', }}
            fontWeight={400}
            size={convertPxToRem(14)}
            color={Colors.black}>
            cancelSubscriptionConformation
          </TextBase>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0px', }}>
            <TextBase
              style={{ fontSize: '14px', textAlign: 'center' }}
              fontWeight={400}
              size={convertPxToRem(14)}
              color={Colors.grey}>
              subscriptionEnd{' '}
            </TextBase>
          </div>
        </ConfirmModal>

        <ConfirmModal
          visible={showReactivateModal}
          status="success"
          okBtnText="RESUME"
          cancelBtnText="CANCEL"
          width="450px"
          hideIcon
          showIcon
          message=""
          fontWeight={600}
          handleOk={handleResumeMembership}
          handleCancel={closeReactivateSubConfirm}
          onClose={closeReactivateSubConfirm}
          isLoading={isReactivatingChildSubscription}>
          <TextBase
            style={{ textAlign: 'center', marginBottom: '20px' }}
            fontWeight={600}
            size={convertPxToRem(24)}
            color={Colors.black}>
            resumeSubscription
          </TextBase>

          <TextBase
            style={{ textAlign: 'center', marginBottom: '50px' }}
            fontWeight={400}
            size={convertPxToRem(14)}
            color={Colors.black}>
            resumeSubscriptionConformation
          </TextBase>
        </ConfirmModal>

        <ConfirmDialog
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title='deleteLocation'
          description='deleteConfirmText'
          descriptionbold={hasPendingOrders ? 'pendingOrderWarning' : ''}
          onOk={handleDeleteChild}
          loading={isLocationDeleting}
        />
      </div>

    </Paper>
  )
};

ActiveLocationSubscription.propTypes = {}
